import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
    AccountsColumnToggle,
    AccountsDensity,
    AccountsPlacement,
} from '@/pages/Console/Accounts/Shared/AccountsToolbar';
import { EMPTY_FILTERS, type ScopedAccountsFilters } from './types';
import { usePersistedAccountsViewConfig } from '@/pages/Console/Accounts/Shared/usePersistedAccountsViewConfig';
import type { ScopedAccountsVM } from './useScopedAccounts';

/** Frame 35's tab strip: two saved views over the board, plus the audit tab. */
export type ScopedAccountsTab = 'accounts' | 'no-level' | 'invites' | 'audit';

/** The two views the tab strip itself applies through `setTab` — see `presetFilters`. */
export type SavedFilterKey = 'all' | 'no-level';

/** Every column of frame 35's table that may be hidden from the "Cột" menu. */
const HIDEABLE_COLUMNS = ['org', 'level', 'sync_status', 'source', 'last_login'] as const;
export type ScopedAccountColumnKey = (typeof HIDEABLE_COLUMNS)[number];

/**
 * The filter snapshot each preset stands for. `includeChildren` is deliberately
 * NOT part of a preset: it is the scope — now pinned `true` with no UI control at
 * all, since the "Phạm vi" bar that used to own it was deleted as redundant with
 * the rail's "Đơn vị" group (see `AccountsScreen`'s docstring). A preset that
 * silently widened or narrowed the branch would change what the operator is
 * looking at, not how it is filtered.
 */
function presetFilters(key: SavedFilterKey, includeChildren: boolean): ScopedAccountsFilters {
    const base: ScopedAccountsFilters = { ...EMPTY_FILTERS, includeChildren };
    switch (key) {
        case 'no-level':
            return { ...base, issue: 'no_level' };
        default:
            return base;
    }
}

/**
 * EAA-1839 (frame 35 completion pass): the PRESENTATION state of the scoped
 * accounts board — tab, rail quick filter, visible columns, row density.
 *
 * Split from `useScopedAccounts` on purpose. That hook owns what the SERVER is
 * asked (filters, paging, sort, selection) and is already long; none of the state
 * here reaches a request. Keeping the two apart is also what makes the tab strip
 * honest: "Chưa có Level" is not a different query shape, it is `issue=no_level`
 * applied through the same `setFilters` a preset chip uses — so the tab, the chip
 * and the rail row can never disagree about what the board is showing.
 *
 * WHAT IS INERT AND WHY. One of frame 35's affordances has no data behind it
 * and says so rather than pretending: the "Lời mời" tab. There is no invitation
 * entity in portal-api. `issueAccount` with `credential_mode: 'invite'` sends
 * one and returns nothing that is later queryable, so a pending-invitation
 * count cannot be produced.
 *
 * NO SAVED-FILTER ROW, and this is the third pass to reach that conclusion.
 * EAA-1820 shipped one for the realm board on 2026-08-03 and deleted it on
 * 2026-08-04 (SRS-Portal-Accounts-Frontend 1.7) because half of it was
 * `localStorage`-backed fake persistence. This ticket reinstated a
 * presets-only version because frame 35 draws one, and the user removed it
 * again on 2026-08-21. What survives is the part that was never the problem:
 * the tab strip's own two saved views, which are plain `setFilters` calls
 * (`presetFilters` below), not stored data. Do not rebuild the row from frame
 * 35 without checking with the user first.
 */
export function useScopedAccountViews(vm: ScopedAccountsVM) {
    const { t } = useTranslation();
    const [tab, setTabState] = useState<ScopedAccountsTab>('accounts');
    const [railQuery, setRailQuery] = useState('');
    const [density, setDensityState] = useState<AccountsDensity>('comfortable');
    // EAA-1839 (2026-08-21, user request): frame 35 draws only the sidebar rail —
    // 'sidebar' is the seed here for the same reason `hiddenColumns`/`density`
    // above seed to their own frame-35 defaults; `usePersistedAccountsViewConfig`'s
    // reconciliation effect below overrides it the moment a stored value resolves.
    const [placement, setPlacementState] = useState<AccountsPlacement>('sidebar');
    // EAA-1839: "Nguồn" (`source`) AND "Đăng nhập" (`last_login`) start hidden —
    // neither has a data source, so every cell in both reads "Không có dữ liệu" and
    // they only spend width on identical placeholders. `last_login` joined `source`
    // for a second, harder reason measured in the running app: with both visible the
    // declared column widths summed past the table's available width at 1366x768, and
    // under `table-layout: fixed` the overflow was taken out of the one column with
    // no width — the identity column, which collapsed to 0px. Two columns of
    // placeholders are not worth the row's identity. Both stay in the "Cột" menu (the
    // position is real, a later backend can fill it) — only the DEFAULT visibility
    // changes, so it must not be dropped from `HIDEABLE_COLUMNS`. This is the SEED,
    // not the last word: `usePersistedAccountsViewConfig` below may override it the moment
    // a stored preference resolves, including the case where the operator
    // deliberately un-hid `source` — a stored value always wins over this seed.
    const [hiddenColumns, setHiddenColumns] = useState<Set<ScopedAccountColumnKey>>(
        new Set<ScopedAccountColumnKey>(['source', 'last_login'])
    );

    /**
     * EAA-1839 (2026-08-21, user request): persist `hiddenColumns`/`density`
     * across reloads — see `usePersistedAccountsViewConfig`'s own docstring for the full
     * reasoning and the four rules it copies from `useResizableColumns`.
     *
     * PRECEDENCE, stated once because it is easy to get backwards: a STORED
     * value always wins over the `source` seed above; the seed only applies
     * when nothing is stored yet. This is expressed by ORDER, not a branch —
     * the seed is the `useState` initial value, and the reconciliation effect
     * below only ever OVERWRITES it once `loadState` resolves to a real
     * `PersistedAccountsViewConfig`; it never re-applies the seed on top of a loaded
     * value. `reconciled` gates the persist effect further down so that this
     * reconciliation always lands in state BEFORE anything gets written back
     * to storage — without it, the very first render's seed value could
     * overwrite a just-loaded value the instant the write effect fired.
     */
    // EAA-1839 (2026-08-24): this board's own `usePersistedViewConfig` was deleted
    // in favour of the shared hook, which is that file parameterised by storage
    // key. `'scoped-accounts'` is the SAME key suffix the local hook hard-coded,
    // at the same version and payload shape, so preferences already in a
    // browser's storage keep loading — this is a de-duplication, not a reset.
    const { loadState, persist } = usePersistedAccountsViewConfig('scoped-accounts', HIDEABLE_COLUMNS);
    const [reconciled, setReconciled] = useState(false);

    useEffect(() => {
        if (loadState === 'pending') return;
        if (loadState !== 'empty') {
            setHiddenColumns(new Set(loadState.hiddenColumns as ScopedAccountColumnKey[]));
            setDensityState(loadState.density);
            // The shared hook returns `undefined` for an absent/unrecognised
            // placement rather than fabricating one, so the seed above is the default.
            setPlacementState(loadState.placement ?? 'sidebar');
        }
        setReconciled(true);
    }, [loadState]);

    useEffect(() => {
        if (!reconciled) return;
        persist({ hiddenColumns: [...hiddenColumns], density, placement });
    }, [reconciled, hiddenColumns, density, placement, persist]);

    const setDensity = useCallback((next: AccountsDensity) => setDensityState(next), []);
    const setPlacement = useCallback((next: AccountsPlacement) => setPlacementState(next), []);

    const { setFilters } = vm;

    // `noLevelCount` comes from the view model, not from a second lookup here — see
    // its docstring there for why frame 35's three copies of that number must share
    // one source.
    const noLevelCount = vm.noLevelCount;

    const applyPreset = useCallback(
        (key: SavedFilterKey) => {
            setFilters((prev) => presetFilters(key, prev.includeChildren));
        },
        [setFilters]
    );

    /** A tab click IS a preset — see this hook's docstring. */
    const setTab = useCallback(
        (next: ScopedAccountsTab) => {
            setTabState(next);
            if (next === 'accounts') applyPreset('all');
            if (next === 'no-level') applyPreset('no-level');
        },
        [applyPreset]
    );

    const columnItems = useMemo<AccountsColumnToggle[]>(
        () =>
            HIDEABLE_COLUMNS.map((key) => ({
                key,
                label: t(`console.hierarchy.scopedAccounts.table.column_${key}`),
                visible: !hiddenColumns.has(key),
            })),
        [t, hiddenColumns]
    );

    const toggleColumn = useCallback((key: string) => {
        setHiddenColumns((prev) => {
            const next = new Set(prev);
            if (next.has(key as ScopedAccountColumnKey)) next.delete(key as ScopedAccountColumnKey);
            else next.add(key as ScopedAccountColumnKey);
            return next;
        });
    }, []);

    const isColumnVisible = useCallback(
        (key: ScopedAccountColumnKey) => !hiddenColumns.has(key),
        [hiddenColumns]
    );

    return {
        tab,
        setTab,
        noLevelCount,

        railQuery,
        setRailQuery,

        density,
        setDensity,
        placement,
        setPlacement,
        columnItems,
        toggleColumn,
        isColumnVisible,
    };
}

export type ScopedAccountViewsVM = ReturnType<typeof useScopedAccountViews>;
